/**
 * Catálogo do onboarding (proposta "Onboarding" do Claude Design, 17/10/2026): setores com os
 * segmentos e palavras de busca, tipos de cliente, alcance e as origens ("como conheceu").
 *
 * O backend continua recebendo as chaves antigas (`industry`, `customer`, `aware`), para não
 * quebrar relatórios nem o Slack: cada setor, cliente e origem diz qual é a sua chave antiga.
 * Os nomes em português ficam aqui; en, es e fr vêm dos arquivos de idioma (chaves `ob_*`).
 */

export interface Segment {
  /** Chave do texto (`ob_seg_<id>`). */
  id: string;
  /** Nome em português (padrão). */
  name: string;
  /**
   * Outras palavras do segmento ("dentista" → Clínica odontológica). Serviam à procura do
   * onboarding, que saiu em 27/10/2026; ficam no catálogo para uma procura futura.
   */
  keywords: string;
}

export interface Sector {
  id: string;
  /** Chave antiga gravada em `crm.industry`. */
  industry: string;
  name: string;
  icon: string;
  segments: Segment[];
}

const slug = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const segs = (rows: Array<[string, string?]>): Segment[] =>
  rows.map(([name, keywords]) => ({ id: slug(name), name, keywords: keywords ?? '' }));

export const SECTORS: Sector[] = [
  {
    id: 'agro',
    industry: 'agriculture_and_livestock',
    name: 'Agronegócio',
    icon: 'potted_plant',
    segments: segs([
      ['Agricultura'],
      ['Pecuária'],
      ['Laticínios'],
      ['Pesca'],
      ['Insumos agrícolas'],
      ['Máquinas agrícolas'],
      ['Cooperativa agrícola'],
    ]),
  },
  {
    id: 'alim',
    industry: 'food_and_beverages',
    name: 'Alimentos e Bebidas',
    icon: 'restaurant',
    segments: segs([
      ['Restaurante'],
      ['Bar'],
      ['Padaria'],
      ['Distribuidora de bebidas'],
      ['Produção de alimentos'],
      ['Cafeteria'],
      ['Pizzaria'],
      ['Confeitaria'],
      ['Hamburgueria'],
      ['Food truck'],
    ]),
  },
  {
    id: 'cons',
    industry: 'architecture_and_engineering',
    name: 'Construção e Engenharia',
    icon: 'foundation',
    segments: segs([
      ['Construtora'],
      ['Arquitetura', 'arquiteto'],
      ['Engenharia civil', 'engenheiro'],
      ['Materiais de construção'],
      ['Design de interiores', 'decoracao'],
      ['Reformas'],
      ['Engenharia elétrica', 'eletricista'],
      ['Paisagismo'],
    ]),
  },
  {
    id: 'com',
    industry: 'online_and_physical_commerce',
    name: 'Comércio (Online e Lojas Físicas)',
    icon: 'shopping_bag',
    segments: segs([
      ['Loja de roupas'],
      ['Supermercado'],
      ['Atacado'],
      ['E-commerce', 'loja virtual'],
      ['Pet shop'],
      ['Móveis'],
      ['Cosméticos'],
      ['Calçados'],
      ['Eletrônicos'],
      ['Papelaria'],
      ['Joalheria'],
      ['Artigos esportivos'],
      ['Floricultura'],
      ['Autopeças'],
      ['Óculos e relógios'],
    ]),
  },
  {
    id: 'edu',
    industry: 'education_and_training',
    name: 'Educação e Treinamento',
    icon: 'school',
    segments: segs([
      ['Escola'],
      ['Faculdade'],
      ['Cursos online'],
      ['Escola de idiomas', 'ingles'],
      ['Treinamento e coaching', 'coach'],
      ['Cursinho pré-vestibular'],
      ['Escola de música'],
      ['Autoescola'],
    ]),
  },
  {
    id: 'ener',
    industry: 'energy_oil_and_gas',
    name: 'Energia',
    icon: 'bolt',
    segments: segs([['Energia solar'], ['Petróleo e gás'], ['Mineração'], ['Saneamento']]),
  },
  {
    id: 'fab',
    industry: 'manufacturing_industry',
    name: 'Fábricas e Indústria',
    icon: 'factory',
    segments: segs([
      ['Automotiva'],
      ['Química'],
      ['Plásticos'],
      ['Máquinas'],
      ['Têxtil'],
      ['Embalagens'],
      ['Metalurgia'],
      ['Móveis planejados'],
      ['Indústria alimentícia'],
    ]),
  },
  {
    id: 'log',
    industry: 'logistics_and_transportation',
    name: 'Logística e Transporte',
    icon: 'local_shipping',
    segments: segs([
      ['Transportadora'],
      ['Importação e exportação'],
      ['Armazenagem'],
      ['Entregas', 'motoboy'],
      ['Mudanças'],
      ['Fretamento'],
    ]),
  },
  {
    id: 'mkt',
    industry: 'marketing_and_advertising',
    name: 'Marketing e Publicidade',
    icon: 'campaign',
    segments: segs([
      ['Agência de marketing digital', 'agencia redes sociais trafego social media'],
      ['Publicidade'],
      ['Relações públicas', 'assessoria de imprensa'],
      ['Gráfica'],
      ['Produção de vídeo', 'filmagem'],
    ]),
  },
  {
    id: 'imo',
    industry: 'real_estate_business',
    name: 'Imóveis',
    icon: 'home',
    segments: segs([
      ['Imobiliária'],
      ['Corretor de imóveis', 'corretor'],
      ['Incorporadora'],
      ['Administração de condomínios', 'sindico'],
    ]),
  },
  {
    id: 'rep',
    industry: 'commercial_representation',
    name: 'Representação Comercial',
    icon: 'handshake',
    segments: segs([['Representante comercial', 'vendedor externo'], ['Distribuidor']]),
  },
  {
    id: 'sau',
    industry: 'health_and_well_being',
    name: 'Saúde e Bem-Estar',
    icon: 'favorite',
    segments: segs([
      ['Clínica odontológica', 'dentista odontologia dente'],
      ['Clínica médica', 'medico consultorio'],
      ['Psicologia', 'psicologo terapia'],
      ['Fisioterapia', 'fisioterapeuta'],
      ['Academia', 'ginastica musculacao personal'],
      ['Estética', 'esteticista'],
      ['Salão de beleza', 'cabeleireiro'],
      ['Veterinária', 'veterinario'],
      ['Nutrição', 'nutricionista'],
      ['Laboratório de análises', 'exames'],
      ['Clínica de imagem', 'raio x ultrassom'],
      ['Farmácia'],
      ['Ótica'],
      ['Fonoaudiologia', 'fonoaudiologo'],
      ['Pilates'],
      ['Barbearia'],
      ['Spa e massagem'],
      ['Home care'],
      ['Hospital'],
      ['Clínica de vacinas'],
      ['Terapias integrativas', 'acupuntura'],
      ['Podologia'],
      ['Dermatologia', 'dermatologista'],
      ['Oftalmologia', 'oftalmologista'],
      ['Pediatria', 'pediatra'],
    ]),
  },
  {
    id: 'seg',
    industry: 'insurance_and_health_plans',
    name: 'Seguros e Planos de Saúde',
    icon: 'shield',
    segments: segs([['Corretora de seguros', 'corretor'], ['Planos de saúde'], ['Previdência']]),
  },
  {
    id: 'fin',
    industry: 'financial_services_and_credit',
    name: 'Finanças e Crédito',
    icon: 'paid',
    segments: segs([
      ['Correspondente bancário'],
      ['Crédito e empréstimos'],
      ['Consórcio'],
      ['Investimentos'],
    ]),
  },
  {
    id: 'srv',
    industry: 'professional_services',
    name: 'Serviços Profissionais',
    icon: 'work',
    segments: segs([
      ['Contabilidade', 'contador'],
      ['Advocacia', 'advogado'],
      ['Consultoria', 'consultor'],
      ['RH e recrutamento'],
      ['Eventos'],
      ['Limpeza e facilities'],
    ]),
  },
  {
    id: 'tec',
    industry: 'technology_and_innovation',
    name: 'Tecnologia e Inovação',
    icon: 'memory',
    segments: segs([
      ['Desenvolvimento de software', 'programador'],
      ['TI e suporte'],
      ['Segurança da informação'],
      ['SaaS'],
    ]),
  },
  {
    id: 'tel',
    industry: 'telecommunications',
    name: 'Telecomunicações',
    icon: 'cell_tower',
    segments: segs([['Provedor de internet'], ['Telefonia']]),
  },
  {
    id: 'tur',
    industry: 'tourism_and_entertainment',
    name: 'Turismo e Entretenimento',
    icon: 'luggage',
    segments: segs([
      ['Hotel e pousada'],
      ['Agência de viagens'],
      ['Esportes'],
      ['Música'],
      ['Fotografia', 'fotografo'],
      ['Artes'],
    ]),
  },
  {
    id: 'gov',
    industry: 'government_and_nonprofit',
    name: 'Governo e Terceiro Setor',
    icon: 'account_balance',
    segments: segs([
      ['ONG'],
      ['Instituição religiosa', 'igreja'],
      ['Associação'],
      ['Órgão público', 'prefeitura'],
    ]),
  },
  { id: 'out', industry: 'others', name: 'Outros', icon: 'more_horiz', segments: [] },
];

export const SECTOR_BY_ID: Record<string, Sector> = Object.fromEntries(
  SECTORS.map((s) => [s.id, s]),
);

export function sectorByIndustry(industry: string | undefined): Sector | undefined {
  return industry ? SECTORS.find((s) => s.industry === industry) : undefined;
}

/** Acha o segmento pelo nome em português (o que vai para `crm.segment`). */
export function findSegment(name: string | undefined): { sector: Sector; segment: Segment } | null {
  if (!name) return null;
  for (const sector of SECTORS) {
    const segment = sector.segments.find((s) => s.name === name);
    if (segment) return { sector, segment };
  }
  return null;
}

export type CustomerId = 'empresas' | 'profissionais' | 'pessoas' | 'todos';

export const CUSTOMERS: Array<{ id: CustomerId; legacy: string; icon: string }> = [
  { id: 'empresas', legacy: 'companies', icon: 'domain' },
  { id: 'profissionais', legacy: 'professionals', icon: 'badge' },
  { id: 'pessoas', legacy: 'people', icon: 'group' },
  { id: 'todos', legacy: 'both', icon: 'language' },
];

export function customerByLegacy(legacy: string | undefined): CustomerId | undefined {
  return CUSTOMERS.find((c) => c.legacy === legacy)?.id;
}

export type ReachId = 'cidade' | 'regiao' | 'estado' | 'pais' | 'online';

export const REACHES: Array<{ id: ReachId; icon: string; wide?: boolean }> = [
  { id: 'cidade', icon: 'apartment' },
  { id: 'regiao', icon: 'radio_button_checked' },
  { id: 'estado', icon: 'map' },
  { id: 'pais', icon: 'flag' },
  { id: 'online', icon: 'language', wide: true },
];

export function isReach(value: string | undefined): value is ReachId {
  return REACHES.some((r) => r.id === value);
}

export type OriginGroup = 'social' | 'ai' | 'people';

export interface Origin {
  id: string;
  /** Chave gravada em `crm.aware` (as antigas continuam iguais). */
  aware: string;
  group: OriginGroup;
  /** Marca: nome próprio e domínio do favicon. */
  brand?: string;
  domain?: string;
  /** Sem marca: ícone e pergunta do detalhe (opcional). */
  icon?: string;
  detail?: boolean;
}

export const ORIGINS: Origin[] = [
  { id: 'google', aware: 'google', group: 'social', brand: 'Google', domain: 'google.com' },
  { id: 'facebook', aware: 'facebook', group: 'social', brand: 'Facebook', domain: 'facebook.com' },
  {
    id: 'instagram',
    aware: 'instagram',
    group: 'social',
    brand: 'Instagram',
    domain: 'instagram.com',
  },
  { id: 'youtube', aware: 'youtube', group: 'social', brand: 'YouTube', domain: 'youtube.com' },
  { id: 'linkedin', aware: 'linkedin', group: 'social', brand: 'LinkedIn', domain: 'linkedin.com' },
  { id: 'tiktok', aware: 'tiktok', group: 'social', brand: 'TikTok', domain: 'tiktok.com' },
  { id: 'whatsapp', aware: 'whatsapp', group: 'social', brand: 'WhatsApp', domain: 'whatsapp.com' },
  { id: 'chatgpt', aware: 'chatgpt', group: 'ai', brand: 'ChatGPT', domain: 'chatgpt.com' },
  { id: 'claude', aware: 'claude', group: 'ai', brand: 'Claude', domain: 'claude.ai' },
  { id: 'gemini', aware: 'gemini', group: 'ai', brand: 'Gemini', domain: 'gemini.google.com' },
  {
    id: 'perplexity',
    aware: 'perplexity',
    group: 'ai',
    brand: 'Perplexity',
    domain: 'perplexity.ai',
  },
  {
    id: 'copilot',
    aware: 'copilot',
    group: 'ai',
    brand: 'Copilot',
    domain: 'copilot.microsoft.com',
  },
  { id: 'metaai', aware: 'meta_ai', group: 'ai', brand: 'Meta AI', domain: 'meta.ai' },
  { id: 'indicacao', aware: 'recommendation', group: 'people', icon: 'handshake', detail: true },
  { id: 'grupos', aware: 'groups', group: 'people', icon: 'group', detail: true },
  { id: 'evento', aware: 'event_webinar', group: 'people', icon: 'calendar_today', detail: true },
  { id: 'curso', aware: 'course', group: 'people', icon: 'school', detail: true },
  { id: 'podcast', aware: 'podcast', group: 'people', icon: 'mic', detail: true },
  { id: 'artigo', aware: 'article', group: 'people', icon: 'article', detail: true },
  { id: 'forum', aware: 'forum', group: 'people', icon: 'forum', detail: true },
  { id: 'outros', aware: 'others', group: 'people', icon: 'more_horiz', detail: true },
];

export const ORIGIN_GROUPS: OriginGroup[] = ['social', 'ai', 'people'];

/** Normaliza para comparar sem acento e sem caixa. */
export function norm(text: string | undefined): string {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
